import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { CheckOk } from '@/icons';
import { Container, NextLink, Text } from '@/atoms';
import {
  Button,
  CardGiveaway,
  Faq,
  Gallery,
  ScrolledGrid,
  WithPaperBorder,
} from '@/molecules';
import { FlexibleContent } from '@/templates';
import { getBaseLayout } from '@/layouts/BaseLayout';

import { getAllRoutes, getInitialProps } from '@/lib/graphql/api';
import {
  FlexibleContentBlocks,
  giveaway,
  giveawayCard,
  imagePartial,
  pageAgeGate,
  pageHeaderFooter,
  pageTheme,
  seo,
} from '@/lib/graphql/partials';
import { PageQuery } from '@/lib/graphql/querys';
import { pagePropsServerTransformer } from '@/lib/graphql/transformers';
import {
  formatGiveawayDate,
  getCathAllSegments,
  getGiveawayState,
  getRedirect,
  GiveawayState,
  removeBaseURL,
} from '@/lib/utils';
import FallBackIMG from '@/assets/images/fallback-product-card-white.jpg';

import type { WpButton } from '@/components/types';
import type { TypeImageGraphQL } from '@/lib/graphql/types';
import type { TypeGalleryProps } from '@/molecules/gallery/types';
import type { GetStaticPaths } from 'next';
import type { NextPageWithLayout } from 'pages/_app';

// TODO: Añadir tipos
const Giveaway: NextPageWithLayout = ({
  pageProps: { giveaway, title, flexibleContent, featuredImage },
  sorteos,
}: any) => {
  const gallery: TypeGalleryProps['slides'] = featuredImage?.node?.sourceUrl
    ? [
        {
          alt: featuredImage.node.alt ?? 'Main Gallery Image',
          src: featuredImage.node.sourceUrl,
          width: featuredImage.node.mediaDetails.width,
          height: featuredImage.node.mediaDetails.height,
        },
      ]
    : [
        {
          alt: 'fallback image',
          src: FallBackIMG.src,
          width: FallBackIMG.width,
          height: FallBackIMG.height,
        },
      ];

  giveaway.gallery?.map((image: TypeImageGraphQL) => {
    if (image?.sourceUrl) {
      gallery.push({
        alt: image?.altText ?? `gallery image ${gallery.length}`,
        src: image.sourceUrl,
        width: image.mediaDetails.width as number,
        height: image.mediaDetails.height as number,
      });
    }
  });

  if (gallery.length === 0)
    gallery.push({
      alt: 'fallback image',
      src: FallBackIMG.src,
      width: FallBackIMG.width,
      height: FallBackIMG.height,
    });

  const giveawayState = getGiveawayState(giveaway.initDate, giveaway.endDate);

  const router = useRouter();

  const [showLegalBases, setShowLegalBases] = useState<boolean>(false);

  useEffect(() => {
    setShowLegalBases(router.query?.bases === 'true');
    return () => {
      setShowLegalBases(false);
    };
  }, [router]);

  const sorteoAbiertos = sorteos.edges.filter(
    (edge: any) =>
      getGiveawayState(
        edge.node.giveaway.initDate,
        edge.node.giveaway.endDate,
      ) !== GiveawayState.FINISHED && router.query.slug !== edge.node.slug,
  );

  return (
    <>
      <Container wrapper>
        <div className="flex flex-col justify-between gap-6 md:flex-row">
          <div className="relative w-full max-w-[280px] self-center md:mx-0 md:max-w-[425px] md:self-auto">
            <Gallery slides={gallery} />
          </div>
          <div className="md:max-w-[500px]">
            <div className="flex flex-col">
              <div className="flex justify-between pb-4">
                <Text
                  as="span"
                  className="u-actions u-actions--chips text-typo-tertiary"
                >
                  {giveawayState === GiveawayState.CURRENT && 'sorteo abierto'}
                  {giveawayState === GiveawayState.FINISHED && 'sorteo cerrado'}
                  {giveawayState === GiveawayState.WAITING &&
                    'sorteo pendiente'}
                </Text>
              </div>

              <Text as="h1" className="u-headline u-headline--h2 pb-6">
                {title}
              </Text>

              <div className="text-left} mb-5 flex flex-col">
                <span
                  style={{
                    color: '#f68217',
                  }}
                  className="u-actions u-actions--nav font-semibold italic"
                >
                  {formatGiveawayDate(giveaway.initDate, giveaway.endDate)}
                </span>
              </div>

              <div className="u-body u-body--s mb-4 text-typo-secondary last:mb-0 md:mb-6">
                <div
                  className="[&_:is(ul,_ol)]:ml-5 [&_:is(ul,_ol)]:!list-disc [&_>_*:not(:last-child,_li)]:mb-2"
                  dangerouslySetInnerHTML={{ __html: giveaway.description }}
                />
              </div>

              {giveaway.winners && (
                <div className="border-t border-dashed border-primary-900 pt-4 md:pt-6">
                  <p className="u-headline u-headline--h3 mb-4">Ganadores</p>

                  <ul className="u-body u-body--s space-y-2 text-primary-600">
                    {giveaway.winners?.map((winner: any, index: number) => (
                      <li key={index} className="flex gap-2">
                        <CheckOk width={20} height={20} />
                        <div
                          dangerouslySetInnerHTML={{ __html: winner.winner }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {giveaway.ctas && (
                <div className="mt-4 flex flex-col gap-4 border-t border-dashed border-primary-900 pt-4 md:mt-6 md:flex-row md:gap-8 md:pt-6">
                  {giveaway.ctas?.map((button: WpButton, index: number) => (
                    <NextLink
                      href={removeBaseURL(button?.link?.url ?? '#')}
                      passHref
                      key={index}
                    >
                      <Button
                        as="a"
                        variant={button.variant}
                        size={button.size}
                        negative={button.negativo}
                      >
                        {button.label}
                      </Button>
                    </NextLink>
                  ))}
                  <Button
                    variant="secondary"
                    size="normal"
                    onClick={() => setShowLegalBases(!showLegalBases)}
                  >
                    ver bases
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>

      {giveaway.legalInfo && showLegalBases && (
        <WithPaperBorder
          border="outside"
          top
          bottom
          borderHeight={30}
          borderColor="var(--tw-color-primary-100)"
        >
          <Container
            wrapper
            wrapperSize="max-w-[600px]"
            backgroundColor="bg-primary-100"
          >
            <Text as="h2" className="mb-4 md:mb-6">
              {giveaway.legalTitle}
            </Text>

            <div className="flex flex-col gap-2">
              {giveaway.legalInfo?.map((faq: any, index: number) => (
                <Faq question={`${index + 1}. ${faq?.title ?? ''}`} key={index}>
                  {faq?.content}
                </Faq>
              ))}
            </div>
          </Container>
        </WithPaperBorder>
      )}

      {sorteoAbiertos.length > 0 && (
        <Container wrapper>
          <h2>Otros sorteos abiertos</h2>
          <ScrolledGrid
            withSteps={false}
            numCol={3}
            minWidthElement="385px"
            className="[&>*]:mb-6 [&>*]:mt-4 [&>*]:md:mt-10"
          >
            {sorteoAbiertos.map((edge: any, index: number) => (
              <CardGiveaway
                key={index}
                title={edge.node.title}
                initDate={edge.node.giveaway.initDate}
                endDate={edge.node.giveaway.endDate}
                description={edge.node.giveaway.card.description}
                link={edge.node.slug}
                image={edge.node.giveaway.card.image}
              />
            ))}
          </ScrolledGrid>
        </Container>
      )}
      {flexibleContent.flexible.length > 0 && (
        <WithPaperBorder
          border="outside"
          top
          bottom
          borderHeight={30}
          borderColor="var(--tw-color-primary-100)"
        >
          <FlexibleContent type="Sorteo" flexible={flexibleContent.flexible} />
        </WithPaperBorder>
      )}
    </>
  );
};

export async function getStaticProps({ params }: any) {
  const { hasRedirect, redirect } = await getRedirect(params?.slug);

  if (hasRedirect && process.env.NEXT_REDIRECTS_SERVER_ISR_ENABLED === 'true')
    return { redirect };

  // TODO: Traerse solo sorteos abiertos si es abierto el sorteo y solo sorteos cerrados si es cerrado el sorteo
  const page = `
    sorteo(id: $slug, idType: URI) {
      id  
      title
      slug
      ${seo}
      ${pageTheme}
      ${pageHeaderFooter}
      ${pageAgeGate}
      featuredImage {
        node ${imagePartial}
      }
      giveaway: ${giveaway}
      flexibleContent: ${FlexibleContentBlocks('Sorteo')}
    }
    sorteos {
      edges {
        node {
          giveaway: ${giveawayCard}
          slug
          title
        }
      }
    }
  `;
  const query = PageQuery(page, '', 'Sorteo');

  return await pagePropsServerTransformer({
    staticProps: await getInitialProps(query, {
      slug: `sorteos/${params?.slug}`,
    }),
    type: 'Page',
    path: `sorteos/${params?.slug}`,
  });
}

export const getStaticPaths = (async () => {
  const giveaways = await getAllRoutes({ type: 'sorteos' });
  const paths = process.env.FULL_DEPLOY ? giveaways : giveaways.slice(0, 1);

  return {
    paths: getCathAllSegments({
      paths,
      segmentName: 'slug',
      base: 'sorteos',
      catchAll: false,
    }),
    fallback: 'blocking',
  };
}) satisfies GetStaticPaths;

Giveaway.getLayout = getBaseLayout;

export default Giveaway;
